/**
 * <bundle-add-component> — adds every line of a bundle to the cart in one action.
 *
 * Progressive enhancement: the markup is a real `/cart/add` form, so the bundle
 * still works with JavaScript disabled or before this module executes (it just
 * navigates to the cart instead of opening the drawer). When `Shopify.actions`
 * is available we intercept the submit and use the standard cart action, which
 * lets the theme's own cart drawer, cart icon and cart items react to the update.
 */
class BundleAddComponent extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-bundle-submit]');
    this.status = this.querySelector('[data-bundle-status]');

    this.form?.addEventListener('submit', this.#onSubmit, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** @returns {Array<{merchandiseId: string, quantity: number}>} */
  get lines() {
    return Array.from(this.querySelectorAll('[data-bundle-variant]')).reduce((lines, input) => {
      const merchandiseId = input.value;
      const quantity = Number(input.dataset.bundleQuantity || 1);

      if (merchandiseId && quantity > 0) lines.push({ merchandiseId, quantity });

      return lines;
    }, []);
  }

  #onSubmit = (event) => {
    const actions = window.Shopify?.actions;

    // No standard actions available — let the browser submit the form normally.
    if (!actions?.updateCart) return;

    event.preventDefault();

    const { lines } = this;
    if (!lines.length) return;

    this.#setBusy(true);

    actions
      .updateCart({ lines }, { event: { context: 'standard-action' } })
      .then((result) => {
        const error = result?.userErrors?.[0]?.message;
        if (error) throw new Error(error);

        this.#setStatus('');
        return actions.openCart?.();
      })
      .catch((error) => {
        // Fall back to the plain form navigation so the customer is never stuck.
        this.#setStatus(this.dataset.errorText || 'Something went wrong. Taking you to your cart.');
        console.warn('[bundle-add] Falling back to form submission:', error);
        this.form?.submit();
      })
      .finally(() => {
        this.#setBusy(false);
      });
  };

  /** @param {boolean} busy */
  #setBusy(busy) {
    if (!this.button) return;

    this.button.disabled = busy;
    this.button.setAttribute('aria-busy', String(busy));
  }

  /** @param {string} message */
  #setStatus(message) {
    if (!this.status) return;

    this.status.textContent = message;
    this.status.hidden = message === '';
  }
}

if (!customElements.get('bundle-add-component')) {
  customElements.define('bundle-add-component', BundleAddComponent);
}
